/**
 * ft-disclosure
 * Collapsible section with smooth animation and keyboard support.
 *
 * Precondition: None
 * Postcondition: Custom element renders collapsible disclosure widget
 *
 * @attr {string} summary - Text for the clickable header (default: "Details")
 * @attr {boolean} open - Boolean attribute to expand content initially
 *
 * @slot summary - Custom summary content (overrides summary attribute)
 * @slot default - Content to show/hide
 *
 * @fires ft-disclosure-toggle - Fired when expanded/collapsed, detail: { open: boolean }
 *
 * @csspart summary - The clickable header button
 * @csspart content - The collapsible content wrapper
 * @csspart icon - The chevron indicator
 */

import { icon } from '../../../shared/icons.js';

class FtDisclosure extends HTMLElement {
  private abortController = new AbortController();
  private isAnimating = false;

  static get observedAttributes(): string[] {
    return ['open', 'summary'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  get open(): boolean {
    return this.hasAttribute('open');
  }

  set open(value: boolean) {
    if (value) {
      this.setAttribute('open', '');
    } else {
      this.removeAttribute('open');
    }
  }

  get summary(): string {
    return this.getAttribute('summary') || 'Details';
  }

  set summary(value: string) {
    this.setAttribute('summary', value);
  }

  connectedCallback(): void {
    this.render();
    this.setupEventListeners();
  }

  disconnectedCallback(): void {
    this.abortController.abort();
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue === newValue || !this.shadowRoot?.children.length) return;

    if (name === 'open') {
      this.animateToggle(newValue !== null);
    }

    if (name === 'summary') {
      const summaryText = this.shadowRoot.querySelector('.summary-text');
      if (summaryText) {
        summaryText.textContent = this.summary;
      }
    }
  }

  // Public API
  toggle(): void {
    this.open = !this.open;
  }

  expand(): void {
    this.open = true;
  }

  collapse(): void {
    this.open = false;
  }

  private render(): void {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(this.getStyles());

    const template = document.createElement('template');
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = '';
      this.shadowRoot.adoptedStyleSheets = [sheet];
      this.shadowRoot.appendChild(template.content.cloneNode(true));

      // Set initial state without animation
      const content = this.shadowRoot.querySelector('.content') as HTMLElement;
      const icon = this.shadowRoot.querySelector('.icon') as HTMLElement;
      if (content && icon) {
        if (this.open) {
          content.style.height = 'auto';
          content.style.opacity = '1';
          icon.style.transform = 'rotate(90deg)';
        } else {
          content.style.height = '0';
          content.style.opacity = '0';
          icon.style.transform = 'rotate(0deg)';
        }
      }
    }
  }

  private getStyles(): string {
    return `
      :host {
        display: block;
        --disclosure-bg: transparent;
        --disclosure-hover-bg: var(--surface, #f4f4f5);
        --disclosure-text: var(--text, #1c1c1e);
        --disclosure-muted: var(--muted, #555555);
        --disclosure-border: var(--divider-color, #e2e4e8);
        --disclosure-icon-color: var(--muted, #555555);
        --disclosure-transition: 150ms ease-out;
      }

      @media (prefers-color-scheme: dark) {
        :host {
          --disclosure-hover-bg: var(--surface, #14161a);
          --disclosure-text: var(--text, #ffffff);
          --disclosure-muted: var(--muted, #a3a3a3);
          --disclosure-border: var(--divider-color, #2a2d33);
          --disclosure-icon-color: var(--muted, #a3a3a3);
        }
      }

      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      .disclosure {
        width: 100%;
      }

      .summary {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 6px 4px;
        background: var(--disclosure-bg);
        border: none;
        cursor: pointer;
        font: 400 13px/1.4 var(--font-sans, Inter, system-ui, sans-serif);
        color: var(--disclosure-muted);
        text-align: left;
        transition: background var(--disclosure-transition), color var(--disclosure-transition);
        border-radius: 4px;
      }

      .summary:hover {
        background: var(--disclosure-hover-bg);
        color: var(--disclosure-text);
      }

      .summary:focus-visible {
        outline: 2px solid var(--primary, #e0524d);
        outline-offset: 2px;
      }

      .icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        color: var(--disclosure-icon-color);
        transition: transform var(--disclosure-transition);
      }

      .icon svg {
        width: 16px;
        height: 16px;
      }

      .summary-text {
        flex: 1;
      }

      .content {
        overflow: hidden;
        transition: height var(--disclosure-transition), opacity var(--disclosure-transition);
      }

      .content-inner {
        padding: 8px 0 0 24px;
      }

      ::slotted(*) {
        color: var(--disclosure-text);
      }

      @media (prefers-reduced-motion: reduce) {
        .summary,
        .icon,
        .content {
          transition: none;
        }
      }
    `;
  }

  private getTemplate(): string {
    return `
      <div class="disclosure" part="disclosure">
        <button
          class="summary"
          part="summary"
          aria-expanded="${this.open}"
          aria-controls="content"
        >
          <span class="icon" part="icon">${icon('chevron-right', 16)}</span>
          <span class="summary-text">
            <slot name="summary">${this.escapeHtml(this.summary)}</slot>
          </span>
        </button>
        <div class="content" part="content" id="content" role="region">
          <div class="content-inner">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }

  private setupEventListeners(): void {
    this.abortController.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    const summary = this.shadowRoot?.querySelector('.summary');

    summary?.addEventListener('click', () => this.toggle(), { signal });

    summary?.addEventListener('keydown', (e) => {
      const key = (e as KeyboardEvent).key;
      if (key === 'Enter' || key === ' ') {
        e.preventDefault();
        this.toggle();
      }
    }, { signal });
  }

  private animateToggle(expanding: boolean): void {
    const content = this.shadowRoot?.querySelector('.content') as HTMLElement;
    const icon = this.shadowRoot?.querySelector('.icon') as HTMLElement;
    const summary = this.shadowRoot?.querySelector('.summary');

    if (!content || this.isAnimating) return;

    this.isAnimating = true;

    // Update ARIA
    summary?.setAttribute('aria-expanded', String(expanding));

    // Rotate icon
    if (icon) {
      icon.style.transform = expanding ? 'rotate(90deg)' : 'rotate(0deg)';
    }

    if (expanding) {
      // Expand: measure height, animate from 0
      content.style.height = 'auto';
      const targetHeight = content.scrollHeight;
      content.style.height = '0';
      content.style.opacity = '0';

      // Force reflow
      content.offsetHeight;

      content.style.height = `${targetHeight}px`;
      content.style.opacity = '1';

      const onEnd = () => {
        content.style.height = 'auto';
        this.isAnimating = false;
        this.dispatchToggleEvent(true);
      };

      content.addEventListener('transitionend', onEnd, { once: true });
    } else {
      // Collapse: set explicit height, animate to 0
      content.style.height = `${content.scrollHeight}px`;
      content.style.opacity = '1';

      // Force reflow
      content.offsetHeight;

      content.style.height = '0';
      content.style.opacity = '0';

      const onEnd = () => {
        this.isAnimating = false;
        this.dispatchToggleEvent(false);
      };

      content.addEventListener('transitionend', onEnd, { once: true });
    }
  }

  private dispatchToggleEvent(open: boolean): void {
    this.dispatchEvent(new CustomEvent('ft-disclosure-toggle', {
      bubbles: true,
      composed: true,
      detail: { open }
    }));
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get('ft-disclosure')) {
  customElements.define('ft-disclosure', FtDisclosure);
}
